import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClipboardCheck,
  Loader2,
  Pencil,
  RefreshCw,
  Search,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  Copy,
  HelpCircle,
  X,
  ExternalLink,
} from 'lucide-react';
import axios from 'axios';
import { SegmentVerification } from '../../types';

interface AuditSegmentRow {
  segmentid: number;
  segmentname: string;
  verification: SegmentVerification | null;
}

interface CountyCorrection {
  original: string;
  repaired: string;
  reason: 'exact' | 'fuzzy';
}

interface UnresolvedCounty {
  original: string;
  normalized: string;
  state: string;
  candidates: string[];
}

interface RepairResult {
  repaired: string[];
  corrections: CountyCorrection[];
  unresolved: UnresolvedCounty[];
  rejected: string[];
}

interface VerifyResult {
  segmentId: number;
  segmentName: string;
  fieldId: number;
  matched: string[];
  inPasteOnly: string[];
  inSegmentOnly: string[];
  nearMisses: Array<{ paste: string; segment: string; distance: number }>;
  exactMatch: boolean;
  pasteNormalized: string[];
  segmentNormalized: string[];
  rejected: string[];
  valuesHash: string;
  verification: SegmentVerification | null;
}

interface Props {
  onEditSegment: (segment: { segmentid: number; segmentname: string }) => void;
}

const OMIT_PICK = '__omit__';

function formatVerifiedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function SegmentAudit({ onEditSegment }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [segments, setSegments] = useState<AuditSegmentRow[]>([]);
  const [queuedSegmentIds, setQueuedSegmentIds] = useState<number[]>([]);
  const [queueOnly, setQueueOnly] = useState(true);
  const [hideAlumni, setHideAlumni] = useState(true);
  const [hideVerified, setHideVerified] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [pastedText, setPastedText] = useState('');
  const [repairing, setRepairing] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [approving, setApproving] = useState(false);
  const [repair, setRepair] = useState<RepairResult | null>(null);
  /** User picks for unresolved rows: original → canonical, or OMIT_PICK to drop */
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [showVerifyHelp, setShowVerifyHelp] = useState(false);
  const [openingCounties, setOpeningCounties] = useState(false);

  const loadMeta = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get<{
        segments: AuditSegmentRow[];
        queuedSegmentIds: number[];
      }>('/api/interspire/segments/audit-meta');
      setSegments(res.data.segments);
      setQueuedSegmentIds(res.data.queuedSegmentIds ?? []);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load segments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const queuedSet = useMemo(() => new Set(queuedSegmentIds), [queuedSegmentIds]);

  const visibleSegments = useMemo(() => {
    const q = search.trim().toLowerCase();
    return segments
      .filter((s) => !queueOnly || queuedSet.has(s.segmentid))
      .filter((s) => !hideAlumni || !/\balumni\b/i.test(s.segmentname))
      .filter((s) => !hideVerified || !s.verification)
      .filter(
        (s) =>
          !q ||
          s.segmentname.toLowerCase().includes(q) ||
          String(s.segmentid).includes(q)
      )
      .sort((a, b) => a.segmentname.localeCompare(b.segmentname));
  }, [segments, queueOnly, hideAlumni, hideVerified, queuedSet, search]);

  const selected =
    selectedId != null ? segments.find((s) => s.segmentid === selectedId) ?? null : null;

  const workingList = useMemo(() => {
    if (!repair) return [] as string[];
    const seen = new Set<string>();
    const out: string[] = [];
    const push = (v: string) => {
      const key = v.trim().toLowerCase().replace(/\s+/g, ' ');
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push(v);
    };
    for (const v of repair.repaired) push(v);
    for (const u of repair.unresolved) {
      const pick = picks[u.original];
      if (pick && pick !== OMIT_PICK) push(pick);
    }
    return out;
  }, [repair, picks]);

  const unresolvedOpen = useMemo(() => {
    if (!repair) return 0;
    return repair.unresolved.filter((u) => picks[u.original] == null || picks[u.original] === '').length;
  }, [repair, picks]);

  const selectSegment = (id: number) => {
    setSelectedId(id);
    setResult(null);
    setActionMsg(null);
  };

  const resetRepairState = () => {
    setRepair(null);
    setPicks({});
    setResult(null);
  };

  const clearPasteForm = () => {
    setPastedText('');
    setRepair(null);
    setPicks({});
    setResult(null);
  };

  /** Keep the paste field in sync with the working cleaned list (including pick resolutions). */
  useEffect(() => {
    if (!repair) return;
    setPastedText(workingList.join('\n'));
  }, [repair, workingList]);

  const handleRepair = async () => {
    if (!pastedText.trim()) {
      setActionMsg('Paste a County, State list before cleaning.');
      return;
    }
    setRepairing(true);
    setActionMsg(null);
    setResult(null);
    try {
      const res = await axios.post<RepairResult>('/api/interspire/segments/audit-repair', {
        pastedText,
      });
      setRepair(res.data);
      setPicks({});
      // workingList effect will fill the textarea with the cleaned list
      const corrected = res.data.corrections.length;
      const unresolved = res.data.unresolved.length;
      const rejected = res.data.rejected.length;
      setActionMsg(
        `Cleaned: ${res.data.repaired.length} ready` +
          (corrected ? `, ${corrected} corrected` : '') +
          (unresolved ? `, ${unresolved} need a pick` : '') +
          (rejected ? `, ${rejected} rejected` : '') +
          '. List updated in the form below.'
      );
    } catch (e: any) {
      setActionMsg(e?.response?.data?.error ?? e?.message ?? 'Repair failed');
      setRepair(null);
    } finally {
      setRepairing(false);
    }
  };

  const openCountiesJson = async () => {
    setOpeningCounties(true);
    try {
      const res = await axios.get('/api/interspire/us-counties', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after a delay so the new tab can load
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      setActionMsg(e?.response?.data?.error ?? e?.message ?? 'Could not open county JSON');
    } finally {
      setOpeningCounties(false);
    }
  };

  const handleVerify = async () => {
    if (selectedId == null) {
      setActionMsg('Select a segment first.');
      return;
    }
    if (!repair) {
      setActionMsg('Run Clean & repair before verifying.');
      return;
    }
    if (workingList.length === 0) {
      setActionMsg('No repaired counties to verify. Resolve or re-paste.');
      return;
    }
    if (unresolvedOpen > 0) {
      setActionMsg(
        `${unresolvedOpen} unresolved row${unresolvedOpen === 1 ? '' : 's'} will be excluded from Verify. Pick a match or leave them out.`
      );
    }
    setVerifying(true);
    setActionMsg(null);
    setResult(null);
    try {
      const res = await axios.post<VerifyResult>(
        `/api/interspire/segments/${selectedId}/audit-verify`,
        { repairedValues: workingList }
      );
      setResult(res.data);
      if (unresolvedOpen > 0) {
        setActionMsg(
          `Verified without ${unresolvedOpen} unresolved row${unresolvedOpen === 1 ? '' : 's'}. Resolve them before Approve.`
        );
      }
    } catch (e: any) {
      setActionMsg(e?.response?.data?.error ?? e?.message ?? 'Verify failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleApprove = async () => {
    if (selectedId == null || !result?.exactMatch || !repair) return;
    if (unresolvedOpen > 0) {
      setActionMsg('Resolve or remove all unresolved counties before approving.');
      return;
    }
    setApproving(true);
    setActionMsg(null);
    try {
      const res = await axios.post<{ success: boolean; verification: SegmentVerification }>(
        `/api/interspire/segments/${selectedId}/audit-approve`,
        { repairedValues: workingList }
      );
      setSegments((prev) =>
        prev.map((s) =>
          s.segmentid === selectedId ? { ...s, verification: res.data.verification } : s
        )
      );
      clearPasteForm();
      setActionMsg('Segment marked as verified. Form cleared — select the next segment.');
    } catch (e: any) {
      setActionMsg(e?.response?.data?.error ?? e?.message ?? 'Approve failed');
    } finally {
      setApproving(false);
    }
  };

  const copyRepaired = async () => {
    if (!workingList.length) return;
    try {
      await navigator.clipboard.writeText(workingList.join('\n'));
      setActionMsg('Copied repaired list to clipboard.');
    } catch {
      setActionMsg('Could not copy to clipboard.');
    }
  };

  const canApprove =
    Boolean(result?.exactMatch) && unresolvedOpen === 0 && workingList.length > 0 && !approving;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-blue-600" />
            Audit Segments
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
            Paste a marketing County, State list, clean it against US counties, then compare to the
            live Interspire segment. Approve only when they match exactly.
          </p>
        </div>
        <button
          type="button"
          onClick={loadMeta}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 min-h-[28rem]">
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 flex flex-col overflow-hidden">
          <div className="p-3 border-b border-gray-100 dark:border-slate-700 space-y-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter segments…"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900"
              />
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={queueOnly}
                  onChange={(e) => setQueueOnly(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600"
                />
                Queue only
                <span className="text-xs text-gray-400">({queuedSegmentIds.length})</span>
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hideAlumni}
                  onChange={(e) => setHideAlumni(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600"
                />
                Hide Alumni
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hideVerified}
                  onChange={(e) => setHideVerified(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600"
                />
                Hide Verified
              </label>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto max-h-[32rem]">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
              </div>
            ) : visibleSegments.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-12 px-4">
                No segments match the current filters. Try unchecking Queue only, Hide Alumni, or Hide
                Verified.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-slate-700">
                {visibleSegments.map((s) => {
                  const active = selectedId === s.segmentid;
                  const inQueue = queuedSet.has(s.segmentid);
                  return (
                    <li key={s.segmentid}>
                      <button
                        type="button"
                        onClick={() => selectSegment(s.segmentid)}
                        className={`w-full text-left px-3 py-2.5 transition-colors ${
                          active
                            ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500'
                            : 'hover:bg-gray-50 dark:hover:bg-slate-700/50 border-l-4 border-transparent'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-gray-900 dark:text-white truncate">
                              {s.segmentname}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">
                              ID {s.segmentid}
                              {inQueue && (
                                <span className="ml-2 text-blue-600 dark:text-blue-400">· In queue</span>
                              )}
                            </div>
                          </div>
                          {s.verification ? (
                            <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-50 text-green-700 border border-green-100 dark:bg-green-900/30 dark:text-green-300">
                              <ShieldCheck className="h-3 w-3" />
                              Verified
                            </span>
                          ) : (
                            <span className="shrink-0 text-[10px] text-gray-400">Not verified</span>
                          )}
                        </div>
                        {s.verification && (
                          <p className="text-[11px] text-gray-400 mt-1">
                            {formatVerifiedAt(s.verification.verifiedAt)}
                            {s.verification.verifiedByUsername
                              ? ` · ${s.verification.verifiedByUsername}`
                              : ''}
                          </p>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4 flex flex-col gap-3">
          {selected ? (
            <>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{selected.segmentname}</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Segment ID {selected.segmentid}
                  {selected.verification
                    ? ` · Verified ${formatVerifiedAt(selected.verification.verifiedAt)}`
                    : ' · Not verified'}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
                  Paste County, State list (one per line)
                </label>
                <textarea
                  value={pastedText}
                  onChange={(e) => {
                    setPastedText(e.target.value);
                    resetRepairState();
                    setActionMsg(null);
                  }}
                  rows={8}
                  placeholder={'Fulton GA\nSan Luis CA\nCobb, GA\n...'}
                  className="w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 font-mono"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleRepair}
                  disabled={repairing || !pastedText.trim()}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {repairing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  Clean & repair
                </button>
                <div className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    onClick={handleVerify}
                    disabled={verifying || !repair || workingList.length === 0}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
                    Verify against segment
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowVerifyHelp(true)}
                    className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                    title="How Clean & Verify works"
                    aria-label="How Clean & Verify works"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={!canApprove}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  title={
                    unresolvedOpen > 0
                      ? 'Resolve all unresolved rows first'
                      : result && !result.exactMatch
                        ? 'Only available when lists match exactly'
                        : 'Mark segment County/State as verified'
                  }
                >
                  {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() =>
                    onEditSegment({ segmentid: selected.segmentid, segmentname: selected.segmentname })
                  }
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                  <Pencil className="h-4 w-4" />
                  Edit segment
                </button>
              </div>

              {actionMsg && (
                <div
                  className={`text-sm rounded-md px-3 py-2 ${
                    actionMsg.includes('verified') ||
                    actionMsg.includes('marked') ||
                    actionMsg.includes('Form cleared') ||
                    actionMsg.startsWith('Cleaned') ||
                    actionMsg.startsWith('Copied')
                      ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                      : 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300'
                  }`}
                >
                  {actionMsg}
                </div>
              )}

              {repair && (
                <div className="border-t border-gray-100 dark:border-slate-700 pt-3 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-medium text-gray-800 dark:text-slate-200">
                      Working list: {workingList.length.toLocaleString()}
                      {unresolvedOpen > 0 && (
                        <span className="ml-2 text-amber-700 dark:text-amber-300 font-normal">
                          · {unresolvedOpen} unresolved
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={copyRepaired}
                      disabled={!workingList.length}
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline disabled:opacity-40"
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copy repaired list
                    </button>
                  </div>

                  {repair.corrections.length > 0 && (
                    <div className="text-xs">
                      <div className="font-semibold text-indigo-700 dark:text-indigo-300 mb-1">
                        Corrected ({repair.corrections.length})
                      </div>
                      <ul className="max-h-28 overflow-auto space-y-0.5 text-gray-600 dark:text-slate-300">
                        {repair.corrections.map((c) => (
                          <li key={`${c.original}->${c.repaired}`}>
                            <span className="line-through opacity-70">{c.original}</span>
                            {' → '}
                            <span className="font-medium text-gray-900 dark:text-white">{c.repaired}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {repair.unresolved.length > 0 && (
                    <div className="text-xs space-y-2">
                      <div className="font-semibold text-amber-700 dark:text-amber-300">
                        Unresolved ({repair.unresolved.length}) — pick a county or leave out
                      </div>
                      <ul className="space-y-2 max-h-48 overflow-auto">
                        {repair.unresolved.map((u) => (
                          <li
                            key={u.original}
                            className="flex flex-col sm:flex-row sm:items-center gap-1.5 rounded-md border border-amber-100 dark:border-amber-900/40 px-2 py-1.5"
                          >
                            <span className="font-mono text-gray-700 dark:text-slate-300 shrink-0 min-w-[8rem]">
                              {u.original}
                            </span>
                            <select
                              value={picks[u.original] ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setPicks((prev) => {
                                  const next = { ...prev };
                                  if (!v) delete next[u.original];
                                  else next[u.original] = v;
                                  return next;
                                });
                                setResult(null);
                              }}
                              className="flex-1 text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1"
                            >
                              <option value="">— choose —</option>
                              <option value={OMIT_PICK}>— leave out —</option>
                              {u.candidates.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {repair.rejected.length > 0 && (
                    <div className="text-xs">
                      <div className="font-semibold text-gray-600 dark:text-slate-300 mb-1">
                        Rejected ({repair.rejected.length})
                      </div>
                      <ul className="max-h-20 overflow-auto text-gray-500 space-y-0.5">
                        {repair.rejected.map((v) => (
                          <li key={v}>{v}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {result && (
                <div className="border-t border-gray-100 dark:border-slate-700 pt-3 space-y-3">
                  <div
                    className={`flex items-center gap-2 text-sm font-medium ${
                      result.exactMatch && unresolvedOpen === 0
                        ? 'text-green-700 dark:text-green-400'
                        : 'text-red-700 dark:text-red-400'
                    }`}
                  >
                    {result.exactMatch && unresolvedOpen === 0 ? (
                      <>
                        <CheckCircle2 className="h-4 w-4" /> Exact match — safe to Approve
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="h-4 w-4" />{' '}
                        {unresolvedOpen > 0
                          ? 'Resolve remaining rows before Approve'
                          : 'Discrepancies found — Edit the segment to fix'}
                      </>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="rounded-md bg-gray-50 dark:bg-slate-900/50 p-2">
                      <div className="text-gray-500">Matched</div>
                      <div className="text-lg font-bold text-gray-900 dark:text-white">
                        {result.matched.length}
                      </div>
                    </div>
                    <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 p-2">
                      <div className="text-amber-700 dark:text-amber-300">In paste only</div>
                      <div className="text-lg font-bold text-amber-800 dark:text-amber-200">
                        {result.inPasteOnly.length}
                      </div>
                    </div>
                    <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-2">
                      <div className="text-red-700 dark:text-red-300">In segment only</div>
                      <div className="text-lg font-bold text-red-800 dark:text-red-200">
                        {result.inSegmentOnly.length}
                      </div>
                    </div>
                  </div>

                  {result.nearMisses.length > 0 && (
                    <div className="text-xs">
                      <div className="font-semibold text-amber-700 dark:text-amber-300 mb-1">
                        Possible typos
                      </div>
                      <ul className="space-y-0.5 text-gray-600 dark:text-slate-300">
                        {result.nearMisses.map((n) => (
                          <li key={`${n.paste}-${n.segment}`}>
                            “{n.paste}” ≈ “{n.segment}” (distance {n.distance})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.inPasteOnly.length > 0 && (
                    <div className="text-xs">
                      <div className="font-semibold text-amber-700 mb-1">Missing from segment</div>
                      <ul className="max-h-28 overflow-auto space-y-0.5 text-gray-600 dark:text-slate-300">
                        {result.inPasteOnly.map((v) => (
                          <li key={v}>{v}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.inSegmentOnly.length > 0 && (
                    <div className="text-xs">
                      <div className="font-semibold text-red-700 mb-1">Extra in segment (not in paste)</div>
                      <ul className="max-h-28 overflow-auto space-y-0.5 text-gray-600 dark:text-slate-300">
                        {result.inSegmentOnly.map((v) => (
                          <li key={v}>{v}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-400 py-16">
              Select a segment on the left to begin.
            </div>
          )}
        </div>
      </div>

      {showVerifyHelp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4">
          <div
            role="dialog"
            aria-labelledby="verify-help-title"
            className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 dark:border-slate-700">
              <h3
                id="verify-help-title"
                className="text-base font-semibold text-gray-900 dark:text-white"
              >
                How Clean &amp; Verify works
              </h3>
              <button
                type="button"
                onClick={() => setShowVerifyHelp(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm text-gray-700 dark:text-slate-300">
              <p>
                <strong className="text-gray-900 dark:text-white">Clean &amp; repair</strong> takes
                your pasted County / State lines and normalizes them against EmailCraft&apos;s
                canonical US county list (same file used for all audit matching):
              </p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Trims whitespace, drops blank lines, and parses common formats (e.g.{' '}
                  <code className="text-xs bg-gray-100 dark:bg-slate-900 px-1 rounded">Fulton GA</code>
                  {' '}→{' '}
                  <code className="text-xs bg-gray-100 dark:bg-slate-900 px-1 rounded">Fulton, GA</code>).
                </li>
                <li>Matches each line to an exact county name + state abbreviation when possible.</li>
                <li>
                  For close misspellings or shortened names in the same state, auto-corrects when
                  there is a clear single winner; otherwise lists candidates for you to pick (or
                  leave out).
                </li>
                <li>Rejects lines that cannot be parsed as a county + state.</li>
                <li>Deduplicates the cleaned list and writes it back into the paste field.</li>
              </ul>
              <p>
                <strong className="text-gray-900 dark:text-white">Verify against segment</strong>{' '}
                compares that cleaned working list to the live Interspire segment&apos;s County/State
                rules (exact set match). Approve is only enabled when every cleaned value matches the
                segment and nothing extra remains in the segment.
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                Reference file on the server:{' '}
                <code className="bg-gray-100 dark:bg-slate-900 px-1 rounded">
                  server/data/us-counties.json
                </code>
              </p>
              <button
                type="button"
                onClick={openCountiesJson}
                disabled={openingCounties}
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline disabled:opacity-50"
              >
                {openingCounties ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4" />
                )}
                Open us-counties.json
              </button>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 dark:border-slate-700 flex justify-end">
              <button
                type="button"
                onClick={() => setShowVerifyHelp(false)}
                className="px-4 py-2 text-sm rounded-lg bg-slate-800 text-white hover:bg-slate-900"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
